import { useEffect, useState, useRef } from "react";
import ActionButton from "@/components/ActionButton";
import api from "@/lib/api";
import { Plus, Search, ExternalLink, Upload, Loader2, Mail, Phone } from "lucide-react";
import ContactDetailModal from "@/components/ContactDetailModal";

export default function Fournisseurs() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [show, setShow] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [detail, setDetail] = useState(null);
  const formRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/fournisseurs", { params: q ? { q } : {} });
    setList(data);
  };
  useEffect(() => { load(); }, [q]);

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/contacts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data); load();
    } catch (err) {
      setImportResult({ error: err.response?.data?.detail || "Erreur d'import" });
    } finally { setImporting(false); }
  };

  const create = async (e) => {
    e?.preventDefault?.();
    const form = formRef.current;
    if (!form || !form.reportValidity()) return;
    const fd = Object.fromEntries(new FormData(form).entries());
    fd.tags = (fd.tags || "").split(",").map((s) => s.trim()).filter(Boolean);
    fd.compte_pro = fd.compte_pro === "on";
    await api.post("/fournisseurs", fd);
    setShow(false); load();
  };

  return (
    <div className="space-y-6" data-testid="fournisseurs-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Sourcing produits</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Fournisseurs</h1>
          <p className="text-sm text-stone-500 mt-1">{list.length} fiche{list.length > 1 ? "s" : ""} · cliquez pour éditer.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <label data-testid="csv-import-button" className="bg-stone-100 hover:bg-stone-200 text-stone-800 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 cursor-pointer border border-stone-200">
            {importing ? <Loader2 size={16} className="animate-spin"/> : <Upload size={16}/>}
            {importing ? "Import…" : "Importer CSV Notion"}
            <input type="file" accept=".csv" onChange={onImport} className="hidden" disabled={importing} data-testid="csv-file-input"/>
          </label>
          <button data-testid="fournisseur-new" onClick={() => setShow(true)} className="bg-stone-900 hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><Plus size={16}/> Ajouter</button>
        </div>
      </div>

      {importResult && (
        <div className={`text-sm rounded-lg px-3 py-2 border ${importResult.error ? "bg-red-50 border-red-200 text-red-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`} data-testid="import-result">
          {importResult.error ? `Erreur : ${importResult.error}` : `Import réussi · ${importResult.fournisseurs} fournisseur(s) · ${importResult.partenaires} partenaire(s) · ${importResult.contacts} contact(s) · ${importResult.skipped} ignoré(s) sur ${importResult.total}`}
        </div>
      )}

      <div className="relative max-w-xl">
        <Search size={16} className="absolute left-3 top-2.5 text-stone-400"/>
        <input data-testid="fournisseur-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Recherche : nom, catégorie, mots-clés, notes, ville…" className="w-full pl-9 pr-3 py-2 border border-stone-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-stone-900"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((f) => (
          <div key={f.id} className="bg-white border border-stone-200 hover:border-stone-300 rounded-2xl p-5 transition cursor-pointer" data-testid={`fournisseur-${f.id}`} onClick={() => setDetail(f)}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display font-semibold text-base truncate">{f.nom}</div>
                {f.societe && f.societe !== f.nom && <div className="text-xs text-stone-500 truncate">{f.societe}</div>}
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {(f.types || []).filter((t) => t !== "fournisseur").map((t) => (
                    <span key={t} className="pill bg-amber-50 text-amber-800 text-[10px] capitalize" data-testid={`fr-${f.id}-also-${t}`}>+ {t}</span>
                  ))}
                  {f.categorie && <span className="pill bg-rose-50 text-rose-800 text-[10px]">{f.categorie}</span>}
                </div>
              </div>
              {f.compte_pro && <span className="pill bg-emerald-50 text-emerald-700 text-[10px] shrink-0">Compte pro</span>}
            </div>
            <div className="mt-3 text-sm space-y-1">
              {f.contact && <div className="text-stone-700 truncate">{f.contact}{f.poste && <span className="text-stone-500"> · {f.poste}</span>}</div>}
              <div className="flex items-center gap-2 text-xs">
                {f.email && (
                  <a onClick={(e) => e.stopPropagation()} href={`mailto:${f.email}`} data-testid={`fr-mail-${f.id}`} className="inline-flex items-center gap-1 text-stone-600 hover:text-stone-900 truncate">
                    <Mail size={11}/> {f.email}
                  </a>
                )}
              </div>
              {f.telephone && (
                <a onClick={(e) => e.stopPropagation()} href={`tel:${f.telephone.replace(/\s/g, '')}`} data-testid={`fr-tel-${f.id}`} className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900">
                  <Phone size={11}/> {f.telephone}
                </a>
              )}
              {f.site && (
                <a onClick={(e) => e.stopPropagation()} href={f.site} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900">
                  <ExternalLink size={11}/> {f.site.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>
            {f.tags?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {f.tags.slice(0, 4).map((t) => <span key={t} className="pill bg-stone-100 text-stone-600 text-[10px]">{t}</span>)}
              </div>
            )}
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full text-center text-stone-400 py-12">Aucun fournisseur trouvé.</div>}
      </div>

      {detail && (
        <ContactDetailModal
          item={detail}
          onClose={() => setDetail(null)}
          onSaved={() => load()}
          onDeleted={() => load()}
        />
      )}

      {show && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form ref={formRef} onSubmit={create} className="bg-white rounded-2xl p-6 max-w-lg w-full" data-testid="fournisseur-form">
            <h3 className="font-display text-2xl font-bold mb-4">Nouveau fournisseur</h3>
            <div className="space-y-3 text-sm">
              <input name="nom" required placeholder="Nom contact ou société" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <input name="societe" placeholder="Société (si différente)" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <input name="categorie" placeholder="Catégorie (ex: Mobilier CHR)" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <div className="grid grid-cols-2 gap-2">
                <input name="contact" placeholder="Contact" className="border border-stone-200 rounded-lg px-3 py-2"/>
                <input name="telephone" placeholder="Téléphone" className="border border-stone-200 rounded-lg px-3 py-2"/>
              </div>
              <input name="email" type="email" placeholder="Email" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <input name="site" placeholder="Site web" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <input name="ville" placeholder="Ville" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <input name="tags" placeholder="Tags (séparés par virgule)" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <input name="keywords" placeholder="Mots-clés de recherche" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border border-stone-200 rounded-lg">Annuler</button>
              <ActionButton type="button" onClick={create} data-testid="fournisseur-submit" successLabel="Créé" className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg font-semibold">Créer</ActionButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
